// @ts-check
/** @typedef {import('../types/api').CommentAccount} CommentAccount */
/** @typedef {import('../types/api').CommentDraft} CommentDraft */
/** @typedef {import('../types/api').PostedComment} PostedComment */

import api from '../utils/apiClient';
import { assertArray } from '../utils/apiGuards';

const commentService = {
  /**
   * @param {string} username
   * @returns {Promise<CommentAccount>}
   */
  searchAccount: (username) => api.get(`/api/admin/comments/accounts/search?username=${encodeURIComponent(username)}`),

  /**
   * @returns {Promise<CommentAccount[]>}
   */
  getAccounts: async () => {
    const data = await api.get('/api/admin/comments/accounts');
    assertArray(data, 'commentService.getAccounts');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<CommentAccount>}
   */
  createAccount: (data) => api.post('/api/admin/comments/accounts', data),

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteAccount: (id) => api.del(`/api/admin/comments/accounts/${id}`),

  /**
   * @param {Object} [data]
   * @returns {Promise<Object>}
   */
  fetchPosts: (data = {}) => api.post('/api/admin/comments/fetch-posts', data),

  /**
   * @param {string[]} postIds
   * @returns {Promise<Object>}
   */
  generateDrafts: (postIds) => api.post('/api/admin/comments/generate-drafts', { post_ids: postIds }),

  /**
   * @param {Object} [filters]
   * @returns {Promise<CommentDraft[]>}
   */
  getDrafts: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.target_account_id) params.append('target_account_id', filters.target_account_id);
    if (filters.location_country) params.append('location_country', filters.location_country);
    const qs = params.toString();
    const data = await api.get(`/api/admin/comments/drafts${qs ? `?${qs}` : ''}`);
    assertArray(data, 'commentService.getDrafts');
    return data;
  },

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<CommentDraft>}
   */
  updateDraft: (id, data) => api.patch(`/api/admin/comments/drafts/${id}`, data),

  /**
   * @param {string[]} draftIds
   * @param {string} action
   * @returns {Promise<Object>}
   */
  bulkApprove: (draftIds, action) =>
    api.patch('/api/admin/comments/drafts/bulk-approve', { draft_ids: draftIds, action }),

  /**
   * @param {string[]} draftIds
   * @returns {Promise<Object>}
   */
  postApproved: (draftIds) => api.post('/api/admin/comments/post-approved', { draft_ids: draftIds }),

  /**
   * @returns {Promise<Object>}
   */
  syncReplies: () => api.post('/api/admin/comments/sync-replies'),

  /**
   * @returns {Promise<PostedComment[]>}
   */
  getPosted: async () => {
    const data = await api.get('/api/admin/comments/posted');
    assertArray(data, 'commentService.getPosted');
    return data;
  },
};

export default commentService;
